import React, { useState, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { useAuth } from '../contexts/AuthContext'
import { trackEngagement } from '../utils/analytics'
import PasswordInput from '../components/common/PasswordInput'
import PasswordStrength from '../components/common/PasswordStrength'
import { motion, AnimatePresence } from 'framer-motion'
import {
  MapPinIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { formContainer, formItem, alertSlideIn, authCard, buttonTap, formShake, successPop } from '../config/animations'

export default function RegisterPage() {
  usePageTitle('Create an Account')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirmation: ''
  })
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [shakeKey, setShakeKey] = useState(0)
  const { register } = useAuth()
  const navigate = useNavigate()

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (error) setError('')
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const handleBlur = (e) => {
    const { name } = e.target
    setTouched(prev => ({ ...prev, [name]: true }))
  }

  // Real-time field validation status
  const fieldStatus = useMemo(() => {
    const status = {}
    if (touched.name && formData.name.trim()) status.name = 'valid'
    if (touched.email && formData.email.includes('@') && formData.email.includes('.')) status.email = 'valid'
    if (touched.password && formData.password.length >= 6) status.password = 'valid'
    if (touched.passwordConfirmation && formData.password && formData.passwordConfirmation === formData.password) {
      status.passwordConfirmation = 'valid'
    }
    return status
  }, [formData, touched])

  const validate = () => {
    const errors = {}
    if (!formData.name.trim()) errors.name = 'Name is required'
    if (!formData.email.trim()) errors.email = 'Email is required'
    else if (!formData.email.includes('@')) errors.email = 'Please enter a valid email address'
    if (formData.password.length < 6) errors.password = 'Password must be at least 6 characters'
    if (formData.password !== formData.passwordConfirmation) {
      errors.passwordConfirmation = 'Passwords do not match'
    }
    return errors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setFieldErrors({})
    setTouched({ name: true, email: true, password: true, passwordConfirmation: true })

    const errors = validate()
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setShakeKey(k => k + 1)
      return
    }

    setIsLoading(true)
    const result = await register(formData.name, formData.email, formData.password)

    if (result.success) {
      trackEngagement.userSignUp()
      navigate('/')
    } else {
      setError(result.error)
      setShakeKey(k => k + 1)
    }

    setIsLoading(false)
  }

  const fieldClasses = (name) => {
    const base = 'input-field-enhanced'
    if (fieldErrors[name]) return `${base} has-error`
    if (fieldStatus[name] === 'valid') return `${base} is-valid`
    return base
  }

  return (
    <div className="auth-page">
      <motion.div
        {...authCard}
        className="w-full max-w-md relative z-10"
      >
        {/* Branding header */}
        <motion.div
          variants={formItem}
          initial="initial"
          animate="animate"
          className="text-center mb-8"
        >
          <motion.div
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl
              bg-gradient-to-br from-primary-100 to-primary-200
              dark:from-primary-100 dark:to-primary-200
              shadow-sm mb-5"
            whileHover={{ scale: 1.05, rotate: -3 }}
            transition={{ type: 'spring', stiffness: 300, damping: 15 }}
          >
            <MapPinIcon className="w-8 h-8 text-primary-600 dark:text-primary-600" />
          </motion.div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text">
            Create your account
          </h1>
          <p className="mt-2 text-base text-text-secondary">
            Join the community and discover local garage sales
          </p>
        </motion.div>

        {/* Card */}
        <motion.div
          key={shakeKey}
          animate={shakeKey > 0 ? formShake.animate : {}}
          className="auth-card"
        >
          {/* Error alert */}
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                {...alertSlideIn}
                className="alert-danger"
                role="alert"
              >
                <ExclamationTriangleIcon className="alert-icon text-red-500 dark:text-red-500" />
                <p>{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <motion.form
            variants={formContainer}
            initial="initial"
            animate="animate"
            onSubmit={handleSubmit}
            noValidate
          >
            {/* Name field */}
            <motion.div variants={formItem} className="input-group mb-5">
              <label htmlFor="register-name" className="input-label">
                Full name <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <div className="relative">
                <input
                  id="register-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Jane Smith"
                  disabled={isLoading}
                  className={fieldClasses('name')}
                  aria-invalid={fieldErrors.name ? 'true' : 'false'}
                  aria-describedby={fieldErrors.name ? 'register-name-error' : undefined}
                />
                <AnimatePresence>
                  {fieldStatus.name === 'valid' && !fieldErrors.name && (
                    <motion.span {...successPop} className="absolute right-3 top-1/2 -translate-y-1/2">
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
              <AnimatePresence mode="wait">
                {fieldErrors.name && (
                  <motion.p
                    key="name-error"
                    initial={{ opacity: 0, y: -4, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -4, height: 0 }}
                    transition={{ duration: 0.15 }}
                    className="input-error"
                    id="register-name-error"
                    role="alert"
                  >
                    {fieldErrors.name}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Email field */}
            <motion.div variants={formItem} className="input-group mb-5">
              <label htmlFor="register-email" className="input-label">
                Email address <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <div className="relative">
                <input
                  id="register-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  className={fieldClasses('email')}
                  aria-invalid={fieldErrors.email ? 'true' : 'false'}
                  aria-describedby={fieldErrors.email ? 'register-email-error' : undefined}
                />
                <AnimatePresence>
                  {fieldStatus.email === 'valid' && !fieldErrors.email && (
                    <motion.span {...successPop} className="absolute right-3 top-1/2 -translate-y-1/2">
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
              <AnimatePresence mode="wait">
                {fieldErrors.email && (
                  <motion.p
                    key="email-error"
                    initial={{ opacity: 0, y: -4, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -4, height: 0 }}
                    transition={{ duration: 0.15 }}
                    className="input-error"
                    id="register-email-error"
                    role="alert"
                  >
                    {fieldErrors.email}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Password field */}
            <motion.div variants={formItem} className="input-group mb-5">
              <label htmlFor="register-password" className="input-label">
                Password <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <PasswordInput
                id="register-password"
                name="password"
                autoComplete="new-password"
                required
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="At least 6 characters"
                disabled={isLoading}
                className={fieldClasses('password')}
                aria-invalid={fieldErrors.password ? 'true' : 'false'}
                aria-describedby={fieldErrors.password ? 'register-password-error' : 'register-password-help'}
              />
              <AnimatePresence mode="wait">
                {fieldErrors.password ? (
                  <motion.p
                    key="password-error"
                    initial={{ opacity: 0, y: -4, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -4, height: 0 }}
                    transition={{ duration: 0.15 }}
                    className="input-error"
                    id="register-password-error"
                    role="alert"
                  >
                    {fieldErrors.password}
                  </motion.p>
                ) : (
                  <PasswordStrength password={formData.password} />
                )}
              </AnimatePresence>
            </motion.div>

            {/* Confirm Password field */}
            <motion.div variants={formItem} className="input-group mb-7">
              <label htmlFor="register-password-confirm" className="input-label">
                Confirm password <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <div className="relative">
                <PasswordInput
                  id="register-password-confirm"
                  name="passwordConfirmation"
                  autoComplete="new-password"
                  required
                  value={formData.passwordConfirmation}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Re-enter your password"
                  disabled={isLoading}
                  className={fieldClasses('passwordConfirmation')}
                  aria-invalid={fieldErrors.passwordConfirmation ? 'true' : 'false'}
                  aria-describedby={fieldErrors.passwordConfirmation ? 'register-confirm-error' : undefined}
                />
                {/* checkmark for password match — positioned outside PasswordInput since it has its own eye toggle */}
              </div>
              <AnimatePresence mode="wait">
                {fieldErrors.passwordConfirmation ? (
                  <motion.p
                    key="confirm-error"
                    initial={{ opacity: 0, y: -4, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -4, height: 0 }}
                    transition={{ duration: 0.15 }}
                    className="input-error"
                    id="register-confirm-error"
                    role="alert"
                  >
                    {fieldErrors.passwordConfirmation}
                  </motion.p>
                ) : fieldStatus.passwordConfirmation === 'valid' ? (
                  <motion.p
                    key="confirm-match"
                    initial={{ opacity: 0, y: -4, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -4, height: 0 }}
                    transition={{ duration: 0.15 }}
                    className="text-sm text-green-600 dark:text-green-500 font-medium mt-1 flex items-center gap-1"
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    Passwords match
                  </motion.p>
                ) : null}
              </AnimatePresence>
            </motion.div>

            {/* Submit button */}
            <motion.div variants={formItem}>
              <motion.button
                type="submit"
                disabled={isLoading}
                whileTap={!isLoading ? buttonTap : undefined}
                whileHover={!isLoading ? { scale: 1.01 } : undefined}
                className="btn-primary w-full min-h-[52px] text-base font-semibold
                  shadow-md hover:shadow-lg transition-shadow"
              >
                {isLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating account...
                  </span>
                ) : (
                  'Create account'
                )}
              </motion.button>
            </motion.div>
          </motion.form>

          {/* Terms note */}
          <motion.p
            variants={formItem}
            initial="initial"
            animate="animate"
            className="text-center mt-5 text-xs text-text-muted leading-relaxed"
          >
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-primary-600 dark:text-primary-600 hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-primary-600 dark:text-primary-600 hover:underline">Privacy Policy</Link>.
          </motion.p>
        </motion.div>

        {/* Login link */}
        <motion.p
          variants={formItem}
          initial="initial"
          animate="animate"
          className="text-center mt-8 text-base text-text-secondary"
        >
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-semibold text-primary-600 dark:text-primary-600
              hover:text-primary-700 dark:hover:text-primary-700
              underline underline-offset-2 decoration-primary-300 dark:decoration-primary-400
              hover:decoration-primary-500 dark:hover:decoration-primary-500
              transition-colors"
          >
            Sign in
          </Link>
        </motion.p>
      </motion.div>
    </div>
  )
}
